<template>
    <v-container fluid>
        <v-card elevation="3" tile>
            <v-card-title class="pa-2 pl-4">
                <v-icon left color="primary">mdi-license</v-icon>
                Lizenzverwaltung
            </v-card-title>
            <v-divider/>

            <v-card-text>
                <v-row>
                    <v-col cols="12" md="6">
                        <v-text-field
                            v-model="search"
                            dense
                            outlined
                            clearable
                            prepend-inner-icon="mdi-magnify"
                            hint="Lizenz suchen"
                            persistent-hint
                        />
                    </v-col>
                </v-row>

                <v-data-table
                    :items="licenses"
                    :headers="headers"
                    :search="search"
                    :loading="loading"
                    sort-by="name"
                >
                    <template v-slot:item.description="{ item }">
                        {{ item.description || '-' }}
                    </template>

                    <template v-slot:item.fee="{ item }">
                        {{ formatMoney(item.fee) }}
                    </template>

                    <template v-slot:item.actions="{ item }">
                        <v-btn x-small color="primary" class="mr-2" @click="openEdit(item)">
                            <v-icon left small>mdi-pencil</v-icon>
                            Bearbeiten
                        </v-btn>
                        <v-btn x-small color="error" @click="remove(item)">
                            <v-icon left small>mdi-delete</v-icon>
                            Löschen
                        </v-btn>
                    </template>
                </v-data-table>
            </v-card-text>

            <v-divider/>
            <v-card-actions>
                <v-btn small color="primary" @click="openCreate()">
                    <v-icon left>mdi-plus</v-icon>
                    Neue Lizenz
                </v-btn>
            </v-card-actions>
        </v-card>

        <v-dialog v-model="editDialog" max-width="600" persistent>
            <v-card>
                <v-card-title class="pb-2 pt-2">
                    <v-icon left>mdi-license</v-icon>
                    {{ editModel.id ? 'Lizenz bearbeiten' : 'Lizenz erstellen' }}
                </v-card-title>
                <v-divider/>

                <v-card-text class="pt-4">
                    <v-text-field
                        v-model="editModel.name"
                        dense
                        outlined
                        hint="Name"
                        persistent-hint
                        prepend-inner-icon="mdi-format-title"
                    />
                    <v-text-field
                        v-model.number="editModel.fee"
                        type="number"
                        min="0"
                        step="1"
                        dense
                        outlined
                        hint="Preis"
                        persistent-hint
                        prepend-inner-icon="mdi-currency-eur"
                    />
                    <v-textarea
                        v-model="editModel.description"
                        dense
                        outlined
                        rows="4"
                        auto-grow
                        hint="Beschreibung"
                        persistent-hint
                        prepend-inner-icon="mdi-text-box-outline"
                    />
                </v-card-text>

                <v-divider/>
                <v-card-actions>
                    <v-btn small color="success" :loading="saving" @click="save()">
                        <v-icon left>mdi-content-save</v-icon>
                        Speichern
                    </v-btn>
                    <v-spacer/>
                    <v-btn small color="grey" @click="closeEdit()">
                        Abbrechen
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </v-container>
</template>

<script>
import Swal from 'sweetalert2';

export default {
    data: () => ({
        loading: false,
        saving: false,
        search: '',
        licenses: [],
        editDialog: false,
        editModel: {
            id: null,
            name: '',
            fee: 0,
            description: '',
        },
        headers: [
            {text: 'Name', value: 'name'},
            {text: 'Beschreibung', value: 'description'},
            {text: 'Preis', value: 'fee'},
            {text: 'Aktionen', value: 'actions', sortable: false},
        ],
    }),
    methods: {
        formatMoney(value) {
            return new Intl.NumberFormat('de-DE', {
                style: 'currency',
                currency: 'EUR',
            }).format(Number(value) || 0);
        },
        fetchLicenses() {
            this.loading = true;
            return this.apiGetCompanyLicenses().then(r => {
                this.licenses = Array.isArray(r.data) ? r.data : [];
            }).catch(err => {
                Swal.fire({
                    icon: 'error',
                    title: 'Lizenzen konnten nicht geladen werden',
                    text: err?.response?.data?.message || err?.message || 'Unbekannter Fehler',
                });
            }).finally(() => {
                this.loading = false;
            });
        },
        openCreate() {
            this.editModel = {
                id: null,
                name: '',
                fee: 0,
                description: '',
            };
            this.editDialog = true;
        },
        openEdit(item) {
            this.editModel = {
                id: item.id,
                name: item.name,
                fee: Number(item.fee) || 0,
                description: item.description || '',
            };
            this.editDialog = true;
        },
        closeEdit() {
            this.editDialog = false;
            this.editModel = {
                id: null,
                name: '',
                fee: 0,
                description: '',
            };
        },
        save() {
            if (!this.editModel.name || this.editModel.name.trim().length === 0) {
                Swal.fire({icon: 'warning', title: 'Name fehlt', text: 'Bitte einen Namen angeben.'});
                return;
            }

            this.saving = true;
            const payload = {
                name: this.editModel.name.trim(),
                fee: Math.max(0, Number(this.editModel.fee) || 0),
                description: (this.editModel.description || '').trim(),
            };

            const req = this.editModel.id
                ? this.apiUpdateCompanyLicense(this.editModel.id, payload)
                : this.apiCreateCompanyLicense(payload);

            req.then(() => {
                this.closeEdit();
                return this.fetchLicenses();
            }).catch(err => {
                Swal.fire({
                    icon: 'error',
                    title: 'Lizenz konnte nicht gespeichert werden',
                    text: err?.response?.data?.message || err?.message || 'Unbekannter Fehler',
                });
            }).finally(() => {
                this.saving = false;
            });
        },
        remove(item) {
            Swal.fire({
                title: 'Lizenz löschen?',
                html: `Möchtest du die Lizenz <b>${item.name}</b> wirklich löschen?`,
                showCancelButton: true,
                cancelButtonText: 'Abbrechen',
                confirmButtonText: 'Löschen',
            }).then(r => {
                if (!r.isConfirmed) return;

                this.loading = true;
                this.apiDeleteCompanyLicense(item.id)
                    .then(() => this.fetchLicenses())
                    .catch(err => {
                        Swal.fire({
                            icon: 'error',
                            title: 'Lizenz konnte nicht gelöscht werden',
                            text: err?.response?.data?.message || err?.message || 'Unbekannter Fehler',
                        });
                    })
                    .finally(() => {
                        this.loading = false;
                    });
            });
        },
    },
    mounted() {
        this.fetchLicenses();
    }
}
</script>
